import { useAnimationFrame, useReducedMotion } from 'framer-motion';
import { useRef, type FC, type RefObject } from 'react';
import { Button } from 'src/components/buttons-and-actions/button';
import { useAppShell } from 'src/components/layout/app-shell/use-app-shell-provider';
import { ChevronUpIcon } from 'src/components/media-and-icons/icons';
import { cn } from 'src/utils/cn';

export const ScrollToTopButton: FC<{
  mainRef?: RefObject<HTMLDivElement>;
}> = ({ mainRef }) => {
  const {
    registerForInfiniteScrolling,
    infiniteScrollingMode,
    setInfiniteScrollingMode,
  } = useAppShell();
  const reducedMotion = useReducedMotion();

  const containerRef = useRef<HTMLDivElement>(null);
  useAnimationFrame(() => {
    if (!containerRef.current) return;

    // new design uses mainRef, but in this special case we support also old design with document.body
    const parent = mainRef?.current ?? document.body;

    // the button should be fixed to the top of the viewport,
    // but we need it to be relative to the main content.
    // that's tricky with css alone, so we use this workaround.
    containerRef.current.style.left = parent.offsetLeft + 'px';
    containerRef.current.style.width = parent.clientWidth + 'px';

    const threshold = 650;
    const tooNarrow = parent.clientHeight - window.innerHeight < threshold;

    if (!registerForInfiniteScrolling || tooNarrow) {
      setInfiniteScrollingMode('default');
    } else if (window.scrollY > threshold) {
      setInfiniteScrollingMode('after-threshold');
    } else {
      setInfiniteScrollingMode('before-threshold');
    }
  });

  return (
    <div
      ref={containerRef}
      className={cn(
        `
          fixed top-[calc(var(--top-bar-height)+70px)] grid w-full
          justify-center
          md:top-[calc(var(--top-bar-height)+30px)]
          legacy:top-[220px]
          legacy:md:top-[180px]
        `,
        infiniteScrollingMode !== 'after-threshold' && 'pointer-events-none'
      )}
    >
      <div
        className={cn(
          `inline-block transform transition-all duration-300 ease-in-out`,
          infiniteScrollingMode === 'after-threshold'
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-xs scale-95 opacity-0'
        )}
      >
        <Button
          tabIndex={-1} // just needed for non-keyboard users
          data-testid="scroll-to-top-button"
          mode="secondary"
          size="small"
          onClick={() => {
            document.documentElement.scrollIntoView({
              behavior: reducedMotion ? 'instant' : 'smooth',
              block: 'start',
            });
          }}
        >
          <div className="flex items-center gap-xs">
            Scroll to Top <ChevronUpIcon size="sm" />
          </div>
        </Button>
      </div>
    </div>
  );
};
